import { PublicClientApplication } from '@azure/msal-browser';
import type { AuthenticationResult } from '@azure/msal-browser';
import type { Department, Kiosk, KioskEnrollment } from '@worklogix/shared';
import { createApiClient } from '@worklogix/web-common';
import { useEffect, useMemo, useState } from 'react';

import { verifyDeviceToken } from '../api';
import { API_BASE } from '../config';

import { setDeviceContext, setDeviceToken } from './deviceToken';

const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID ?? '';
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID ?? '';
const API_SCOPE = import.meta.env.VITE_ENTRA_API_SCOPE ?? '';

/**
 * True when this bundle was built with the Entra values admin sign-in needs. A
 * kiosk built without them still pairs by pasted token; it just has no admin
 * button.
 */
export const ADMIN_ENROLL_AVAILABLE = TENANT_ID !== '' && CLIENT_ID !== '' && API_SCOPE !== '';

/**
 * The kiosk's OWN MSAL instance, not web-common's AuthProvider. That one renews
 * tokens silently through a hidden iframe, which can never work here: the kiosk
 * serves `X-Frame-Options: DENY` / `frame-ancestors 'none'` and iPad Safari blocks
 * the third-party cookies anyway, so it died with `monitor_window_timeout`
 * (2026-10-29). Instead the access token is taken straight from the sign-in
 * redirect result and held in memory — no silent renewal is ever attempted.
 */
let pca: PublicClientApplication | undefined;
function getPca(): PublicClientApplication {
  pca ??= new PublicClientApplication({
    auth: {
      clientId: CLIENT_ID,
      authority: `https://login.microsoftonline.com/${TENANT_ID}`,
      redirectUri: window.location.origin,
      navigateToLoginRequestUrl: false,
    },
    // Session-scoped: nothing of the admin's survives a closed tab on a shared clock.
    cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false },
  });
  return pca;
}

/**
 * Drop every MSAL cache entry from this tablet, so the admin's session is not
 * left behind on a shared clock. Local only — no redirect to the Entra sign-out
 * page, which would navigate the kiosk away.
 */
function forgetAdminSession(): void {
  for (const store of [window.localStorage, window.sessionStorage]) {
    const doomed: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key !== null && (key.startsWith('msal.') || key.includes(CLIENT_ID))) {
        doomed.push(key);
      }
    }
    for (const key of doomed) {
      store.removeItem(key);
    }
  }
}

function signIn(): Promise<void> {
  const instance = getPca();
  return instance
    .initialize()
    .then(() => instance.loginRedirect({ scopes: [API_SCOPE], prompt: 'select_account' }));
}

type Mode = 'existing' | 'new';

/**
 * Admin-assisted pairing: an admin signs in with Entra ON the tablet and picks an
 * existing kiosk (its token is rotated) or registers a new one. Uses the same
 * /admin/kiosks endpoints as web-app's KiosksTab; the API enforces the admin role.
 */
export function AdminEnroll({ onCancel }: { onCancel: () => void }): JSX.Element {
  const [auth, setAuth] = useState<AuthenticationResult | null>(null);
  const [checking, setChecking] = useState(true);

  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<Mode>('existing');
  const [kioskId, setKioskId] = useState('');
  const [name, setName] = useState('');
  const [deptId, setDeptId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Complete a returning sign-in redirect; its result carries the API token.
  useEffect(() => {
    let active = true;
    const instance = getPca();
    void instance
      .initialize()
      .then(() => instance.handleRedirectPromise())
      .then((result) => {
        if (active && result !== null && result.accessToken !== '') {
          setAuth(result);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setError(err instanceof Error ? err.message : 'Sign-in failed.');
        }
      })
      .finally(() => {
        if (active) {
          setChecking(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const client = useMemo(
    () =>
      createApiClient({
        baseUrl: API_BASE,
        getToken: () => Promise.resolve(auth?.accessToken ?? ''),
      }),
    [auth]
  );

  useEffect(() => {
    if (auth === null) {
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    void Promise.all([
      client.request<Kiosk[]>('/admin/kiosks'),
      client.request<Department[]>('/admin/departments'),
    ])
      .then(([ks, ds]) => {
        if (active) {
          setKiosks([...ks].sort((a, b) => a.name.localeCompare(b.name)));
          setDepartments([...ds].sort((a, b) => a.name.localeCompare(b.name)));
          if (ks.length === 0) {
            setMode('new');
          }
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setError(
            err instanceof Error
              ? `Could not load kiosks (${err.message}). This account may not be an admin.`
              : 'Could not load kiosks.'
          );
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [client, auth]);

  const deptName = (id: string): string => departments.find((d) => d.id === id)?.name ?? '';

  const canSubmit =
    !busy && (mode === 'existing' ? kioskId !== '' : name.trim() !== '' && deptId !== '');

  const submit = async (): Promise<void> => {
    if (!canSubmit) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let enrollment: KioskEnrollment;
      if (mode === 'existing') {
        const target = kiosks.find((k) => k.id === kioskId);
        // A revoked kiosk rejects every token, so bring it back before minting.
        if (target !== undefined && !target.active) {
          await client.request<Kiosk>(`/admin/kiosks/${encodeURIComponent(kioskId)}`, {
            method: 'PATCH',
            body: { active: true },
          });
        }
        enrollment = await client.request<KioskEnrollment>(
          `/admin/kiosks/${encodeURIComponent(kioskId)}/rotate-token`,
          { method: 'POST' }
        );
      } else {
        enrollment = await client.request<KioskEnrollment>('/admin/kiosks', {
          method: 'POST',
          body: { name: name.trim(), departmentId: deptId },
        });
      }
      // Same validate-then-persist as a pasted token.
      const current = await verifyDeviceToken(enrollment.deviceToken);
      setDeviceContext({
        departmentId: current.departmentId,
        departmentName: current.departmentName,
      });
      forgetAdminSession();
      setDeviceToken(enrollment.deviceToken);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Pairing failed. Try again.');
      setBusy(false);
    }
  };

  const cancel = (): void => {
    forgetAdminSession();
    onCancel();
  };

  if (checking) {
    return (
      <div className="card pairing">
        <h1>Admin pairing</h1>
        <p>Checking sign-in…</p>
      </div>
    );
  }

  if (auth === null) {
    return (
      <div className="card pairing">
        <h1>Admin pairing</h1>
        <p>Sign in with a WorkLogix admin account to bind this tablet to a kiosk.</p>
        <button
          type="button"
          onClick={() => {
            forgetAdminSession();
            void signIn().catch((err: unknown) => {
              setError(err instanceof Error ? err.message : 'Sign-in failed.');
            });
          }}
        >
          Sign in
        </button>
        <button type="button" className="pairing__secondary" onClick={cancel}>
          Back
        </button>
        {error !== null && <p className="pairing__error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="card pairing">
      <h1>Admin pairing</h1>
      <p>Signed in as {auth.account.name ?? auth.account.username}.</p>

      {loading ? (
        <p>Loading kiosks…</p>
      ) : (
        <>
          <div className="pairing__modes" role="radiogroup">
            <label>
              <input
                type="radio"
                checked={mode === 'existing'}
                onChange={() => setMode('existing')}
                disabled={busy || kiosks.length === 0}
              />{' '}
              Existing kiosk
            </label>
            <label>
              <input
                type="radio"
                checked={mode === 'new'}
                onChange={() => setMode('new')}
                disabled={busy}
              />{' '}
              New kiosk
            </label>
          </div>

          {mode === 'existing' ? (
            <>
              <select
                className="pairing__field"
                value={kioskId}
                onChange={(e) => setKioskId(e.target.value)}
                disabled={busy}
              >
                <option value="">Select a kiosk</option>
                {kiosks.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name} — {deptName(k.departmentId)}
                    {k.active ? '' : ' (revoked)'}
                  </option>
                ))}
              </select>
              <p className="pairing__note">
                This issues the kiosk a new token. Any other tablet still paired as this kiosk
                stops working.
              </p>
            </>
          ) : (
            <>
              <input
                className="pairing__field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Kiosk name (e.g. Blend Line 2 Tablet)"
                maxLength={200}
                disabled={busy}
              />
              <select
                className="pairing__field"
                value={deptId}
                onChange={(e) => setDeptId(e.target.value)}
                disabled={busy}
              >
                <option value="">Select a department</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </>
          )}

          <button type="button" onClick={() => void submit()} disabled={!canSubmit}>
            {busy ? 'Pairing…' : 'Set this kiosk'}
          </button>
        </>
      )}
      <button type="button" className="pairing__secondary" onClick={cancel} disabled={busy}>
        Cancel and sign out
      </button>
      {error !== null && <p className="pairing__error">{error}</p>}
    </div>
  );
}

/** True when an MSAL sign-in redirect is returning to this page. */
export function adminSessionPending(): boolean {
  if (!ADMIN_ENROLL_AVAILABLE) {
    return false;
  }
  const hash = window.location.hash;
  return hash.includes('code=') || hash.includes('error=');
}
